import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      String(event.target.dataset.productId ?? '') !== String(this.dataset.productId ?? '')
    ) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

/**
 * Prefer main PDP price block over `price-per-meal` (`.artie-price-per-meal`) when syncing description `(price)`.
 * @param {ParentNode} root
 * @param {string} pidEsc - CSS.escape'd numeric product id, or ''
 */
function findPriceContainerForDescriptionSync(root, pidEsc) {
  if (pidEsc) {
    const main = root.querySelector(
      `product-price[data-product-id="${pidEsc}"]:not(.artie-price-per-meal) [ref="priceContainer"]`
    );
    if (main) return main;
    const anyPid = root.querySelector(`product-price[data-product-id="${pidEsc}"] [ref="priceContainer"]`);
    if (anyPid) return anyPid;
  }
  const notPerMeal = root.querySelector('product-price:not(.artie-price-per-meal) [ref="priceContainer"]');
  if (notPerMeal) return notPerMeal;
  return root.querySelector('product-price [ref="priceContainer"]');
}

/**
 * Same intent as Liquid `variant.price | money`: one inline money string for description `(price)`.
 * Avoids pasting full `priceContainer` HTML (block `<div>`s inside `<span>` / `<p>` breaks the DOM).
 * @param {HTMLElement} container
 */
function primaryMoneyTextFromPriceContainer(container) {
  const saleWrap = container.querySelector('.price__sale');
  if (saleWrap && !saleWrap.classList.contains('price__hidden')) {
    const saleEl =
      saleWrap.querySelector('.price-item--sale.price') ||
      saleWrap.querySelector('.price-item--sale') ||
      saleWrap.querySelector('span.price');
    if (saleEl?.textContent) return saleEl.textContent.trim();
  }
  const regularEl =
    container.querySelector('.price__regular:not(.price__hidden) .price') ||
    container.querySelector('.price__regular .price');
  if (regularEl?.textContent) return regularEl.textContent.trim();
  const rangeEl = container.querySelector('.price-item__group .price');
  if (rangeEl?.textContent) return rangeEl.textContent.trim();
  const perMeal = container.querySelector('.price-per-meal__value');
  if (perMeal?.textContent) return perMeal.textContent.trim();
  return container.textContent.trim();
}

/**
 * `product-description` block: `(price)` → `<span class="js-price" data-product-id>`.
 * On `variant:update`, sets inline text from the main price block (not full price HTML).
 */
function syncDescriptionJsPriceSpans(event) {
  const data = /** @type {VariantUpdateEvent} */ (event).detail?.data;
  const picker = event.target;
  if (!(picker instanceof HTMLElement) || !data?.html) return;

  const eventProductId = String(data.productId ?? '');
  const pickerProductId = String(picker.dataset.productId ?? '');
  if (eventProductId !== pickerProductId) return;

  const pidEsc = eventProductId ? CSS.escape(eventProductId) : '';
  let source =
    findPriceContainerForDescriptionSync(data.html, pidEsc) ||
    findPriceContainerForDescriptionSync(document, pidEsc);

  if (!source) return;

  const moneyText = primaryMoneyTextFromPriceContainer(source);
  if (!moneyText) return;

  const selector = eventProductId
    ? `span.js-price[data-product-id="${CSS.escape(eventProductId)}"]`
    : 'span.js-price';
  document.querySelectorAll(selector).forEach((el) => {
    el.textContent = moneyText;
  });
}

let descriptionJsPriceSyncBound = false;
if (!descriptionJsPriceSyncBound) {
  descriptionJsPriceSyncBound = true;
  document.addEventListener(ThemeEvents.variantUpdate, syncDescriptionJsPriceSpans);
}
